import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatMoney, formatDate, formatDateRange } from "@/lib/api";
import { displayFor, effectiveStatus } from "@/lib/bills-util";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Plus, Flame, Landmark, PiggyBank, CreditCard, Wallet, Receipt, AlertTriangle } from "lucide-react";
import AccountBalanceCell from "@/components/AccountBalanceCell";
import { AddOneTimeIncomeForm } from "@/components/IncomeControls";
import { BillModal } from "@/pages/app/bills/BillModal";
import { IncomeModal } from "@/components/IncomeModal";
import StatusBadge from "@/components/StatusBadge";
import { SECTION } from "@/components/pay-period-forecast/sections-palette";
import {
  SectionHeader,
  LinkRow,
  SubRow,
  LinkSubRow,
  TotalRow,
  MutedRow,
} from "@/components/pay-period-forecast/Rows";
import { PendingDetailModal } from "@/components/pay-period-forecast/PendingDetailModal";
import GroupedIncomeList from "@/components/pay-period-forecast/GroupedIncomeList";
import EmailSnapshotButton from "@/components/pay-period-forecast/EmailSnapshotButton";
import Step2BillBuckets from "@/components/pay-period-forecast/Step2BillBuckets";

// Section palette is imported from ./pay-period-forecast/sections-palette.
// Presentational sub-components (SectionHeader, LinkRow, SubRow, LinkSubRow,
// TotalRow, MutedRow) live in ./pay-period-forecast/Rows; drill-in modals
// (BillDetailModal, PendingDetailModal) live in their own files there too.

// Format-B pay-period forecast. Reads top-to-bottom as a subtraction story:
// what you have on hand → what's incoming → what's going out → what's left.
export default function PayPeriodForecast({
  currentPP, bills = [], incomes = [], pending = [], fundingSources = [],
  onBillUpdated, onSourceUpdated, onIncomeChanged, onPendingChanged,
  // Fires whenever a new bill or income is CREATED via the in-step modals.
  // Parent uses this to refetch all forecast slices (safer than optimistic
  // updates for create-flows that also affect recurring chains).
  onRefresh,
  // View-mode toggle: "pay-period" | "month". Parent owns the state and
  // synthesizes a monthly currentPP window when needed. Same component
  // re-renders with a different window — no other UI changes.
  viewMode = "pay-period",
  onViewModeChange,
  viewLabel,
  // Optional Safe / Attention / At risk badge rendered inline next to the
  // header title. Provided by the Dashboard's CurrentPayPeriodSection when
  // in pay-period view (moved here from the old outer wrapper card that
  // was flattened away for mobile-space reasons).
  statusLabel,
  // Number of consecutive ISO weeks the user has updated at least one
  // funding-source balance in. Powers the streak chip inside the Real
  // Balance box on the Dashboard — encourages weekly habit formation.
  balanceEditStreak = 0,
  // NEW (Feb 2026): parent (CurrentPayPeriodSection) precomputes the
  // band-sorted per-period bill list and the current user record so this
  // component can render the inline Reconcile bill list inside Step 2.
  // Merged from the old standalone Card B (item 6 in the Dashboard
  // cleanup batch).
  periodBills = null,
  user = null,
  onReschedule,
  setBillStatus,
}) {
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const todayIso = useMemo(() => today.toISOString().slice(0, 10), [today]);
  const [openBank, setOpenBank] = useState(false);
  const [openIncoming, setOpenIncoming] = useState(false);
  // Step 2 (Unpaid bills) auto-opens on mount if there are any past-due
  // bills, so at-risk users see the actionable Reconcile list without an
  // extra tap. Ported from the old standalone Card B behaviour when Card B
  // was merged into Step 2 (Feb 2026).
  const hasOverdueBills = useMemo(() => (bills || []).some((b) => {
    if (b.status === "cleared") return false;
    if (b.status === "paid") return false;
    return new Date(b.due_date + "T00:00:00") < today;
  }), [bills, today]);
  const [openBills, setOpenBills] = useState(hasOverdueBills);
  // In-page create modals opened from Step 2 / Step 3 CTAs — keeps users on
  // the Dashboard instead of bouncing them to the Bills or Income Sources
  // pages.
  const [showBillModal, setShowBillModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  // Collapsed by default — keeps the visual weight on paychecks that are
  // still coming; users click "Previous Pay Received…" to reveal received
  // rows in the earlier subtle style.
  const [showReceivedGroup, setShowReceivedGroup] = useState(false);
  // Which detail modal is open. Shape: null | { kind, ...payload }.
  //   kind = "past-due"           → drill into past-due bills bucket
  //   kind = "coming-up"          → drill into upcoming-in-period bills bucket
  //   kind = "pending"            → drill into a source's (or unassigned) pending items
  const [drillIn, setDrillIn] = useState(null);
  // Dismissible negative-balance warning state — declared here (before the
  // early return) so React sees the same hook call order every render, even
  // when currentPP is briefly null while data loads. Warnings dismiss
  // PER-ACCOUNT and PER-SESSION (sessionStorage) so users can hide the
  // ones they've noted while they go transfer money, but forgetting them
  // across app opens would be dangerous — hence NOT localStorage.
  const [dismissedNegKeys, setDismissedNegKeys] = useState(() => {
    try {
      const raw = window.sessionStorage.getItem("wmrb_dismissed_neg_accts");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const dismissNegKey = (key) => {
    setDismissedNegKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      try { window.sessionStorage.setItem("wmrb_dismissed_neg_accts", JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  // (Auto-open-Unpaid-Bills-when-there-are-unpaid-in-period logic removed
  // per user feedback — all sections now default CLOSED for a clean first
  // impression. The header rows themselves still surface the totals at a
  // glance; users expand only what they want to review.)

  if (!currentPP) return null;

  // ---- Balances-freshness stamp (drives Step 4 header + 2-week nudge) ----
  // "Updated" here = last time the user touched a raw money input we can't
  // derive from anywhere else: bank-account balance OR pending charge. Bill
  // status changes intentionally do NOT count — the bank balance being
  // stale is what actually makes the forecast a lie.
  const balanceStamps = fundingSources
    .filter((f) => f.contributes_to_total_balance && f.current_source_balance_optional != null)
    .map((f) => f.balance_updated_at || f.updated_at)
    .filter(Boolean);
  const pendingStamps = (pending || []).map((p) => p.created_at).filter(Boolean);
  const balancesLastEditedIso = [...balanceStamps, ...pendingStamps]
    .sort()
    .slice(-1)[0] || null;
  const balancesLastEditedDate = balancesLastEditedIso ? new Date(balancesLastEditedIso) : null;
  const nowMs = Date.now();
  const daysSinceEdit = balancesLastEditedDate
    ? Math.max(0, Math.floor((nowMs - balancesLastEditedDate.getTime()) / 86400000))
    : null;
  const humanizeDaysAgo = (days) => {
    if (days == null) return "";
    if (days === 0) return "today";
    if (days === 1) return "1 day ago";
    if (days < 14) return `${days} days ago`;
    if (days < 30) {
      const w = Math.round(days / 7);
      return `${w} week${w === 1 ? "" : "s"} ago`;
    }
    if (days < 365) {
      const m = Math.round(days / 30);
      return `${m} month${m === 1 ? "" : "s"} ago`;
    }
    return "over a year ago";
  };
  // Grammar-friendly duration for the "It's been X since..." banner — same
  // numbers as humanizeDaysAgo but WITHOUT the trailing "ago" so the
  // sentence reads naturally ("It's been 2 weeks" not "It's been 2 weeks ago").
  const humanizeDuration = (days) => humanizeDaysAgo(days).replace(/ ago$/, "");
  const balancesAreStale = daysSinceEdit != null && daysSinceEdit >= 14;

  // Opens Step 1 (Bank & Cash) and scrolls it into view. Wired to both the
  // "Update balances →" inline link inside the Real Balance box AND the
  // bigger "Update balances" button inside the 2-week nudge banner — so
  // however the user notices the staleness, one tap gets them straight to
  // where each account balance is inline-editable.
  const openStep1AndScroll = () => {
    setOpenBank(true);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="ppf-section-bank"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const periodStart = new Date(currentPP.period_start + "T00:00:00");
  const periodEnd = new Date(currentPP.period_end + "T00:00:00");

  // ---- Cash accounts + pending-to-clear per account ----
  // Two source lists:
  //   • allDisplaySources — everything with a balance (used for rendering).
  //     Now includes accounts flagged `contributes_to_total_balance=false`
  //     (e.g. Savings/Reserve) so users still see them in Step 1 as a
  //     "protected" reserve row instead of vanishing.
  //   • cashSources — only accounts that count toward Real Balance (used
  //     for all forecast math). Same filter as before.
  const allDisplaySources = fundingSources.filter(
    (f) => f.current_source_balance_optional != null
  );
  const cashSources = allDisplaySources.filter(
    (f) => f.contributes_to_total_balance
  );
  const reserveSources = allDisplaySources.filter(
    (f) => !f.contributes_to_total_balance
  );
  const reserveTotal = reserveSources.reduce(
    (s, f) => s + Number(f.current_source_balance_optional || 0), 0
  );
  const bankTotalRaw = cashSources.reduce(
    (s, f) => s + Number(f.current_source_balance_optional || 0), 0
  );
  // Bills-planned earmark math: total balance across accounts categorised
  // as `bills_planned`. Used to power the "your Bills & Planned balance
  // doesn't cover your bills" nudge below Step 1.
  const billsPlannedTotal = cashSources
    .filter((f) => (f.account_category || "general_spending") === "bills_planned")
    .reduce((s, f) => s + Number(f.current_source_balance_optional || 0), 0);
  // General Spending low-balance nudge math: total balance across the
  // user's General Spending accounts. Compared against
  // user.general_spending_low_threshold to decide whether to render the
  // "General Spending is running low" nudge.
  const generalSpendingAccounts = cashSources.filter(
    (f) => (f.account_category || "general_spending") === "general_spending"
  );
  const generalSpendingTotal = generalSpendingAccounts.reduce(
    (s, f) => s + Number(f.current_source_balance_optional || 0), 0
  );
  const generalSpendingThreshold = Number(user?.general_spending_low_threshold ?? 100);
  // Money that's already out of the account but not cleared yet. Uses
  // displayFor.notCleared so autopay past-due (auto-drifted to pending)
  // and manually-marked-pending bills both reduce available bank balance.
  //
  // We track the underlying items behind each source's pending amount so
  // users can click "Pending to clear" and see WHICH bills/charges make it
  // up (they previously couldn't reconcile the number to specific rows).
  //
  // pendingItemsBySource: sourceId -> [{ kind: 'bill'|'pending', name, date, amount }]
  const pendingBySource = new Map();
  const pendingItemsBySource = new Map();
  const pendingUnassignedItems = [];
  let pendingUnassigned = 0;
  const pushItem = (map, key, item) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  };
  for (const b of bills) {
    if (!displayFor(b, today).notCleared) continue;
    const due = new Date(b.due_date + "T00:00:00");
    if (due > periodEnd) continue;
    const amt = Number(b.estimated_due_amount);
    const source = b.funding_source_id ? cashSources.find((s) => s.id === b.funding_source_id) : null;
    const item = {
      kind: "bill", id: b.id, name: b.name, date: b.due_date, amount: amt,
      is_autopay: !!b.is_autopay,
      sourceName: source ? source.name : "Unassigned",
    };
    if (source) {
      pendingBySource.set(b.funding_source_id, (pendingBySource.get(b.funding_source_id) || 0) + amt);
      pushItem(pendingItemsBySource, b.funding_source_id, item);
    } else {
      pendingUnassigned += amt;
      pendingUnassignedItems.push(item);
    }
  }
  for (const p of pending) {
    const amt = Number(p.amount);
    const source = p.funding_source_id ? cashSources.find((s) => s.id === p.funding_source_id) : null;
    const item = {
      kind: "pending", id: p.id, name: p.description || p.name || "Pending charge",
      date: p.charged_at || p.date || null, amount: amt,
      funding_source_id: p.funding_source_id || null,
      sourceName: source ? source.name : "Unassigned",
    };
    if (source) {
      pendingBySource.set(p.funding_source_id, (pendingBySource.get(p.funding_source_id) || 0) + amt);
      pushItem(pendingItemsBySource, p.funding_source_id, item);
    } else {
      pendingUnassigned += amt;
      pendingUnassignedItems.push(item);
    }
  }
  const pendingTotal = Array.from(pendingBySource.values()).reduce((s, v) => s + v, 0) + pendingUnassigned;
  const availableBank = bankTotalRaw - pendingTotal;

  // Per-account "safe to spend" (raw balance minus pending charges assigned
  // to that account). Used to power the always-visible negative-balance
  // warning at the Step-1 header level so a single underwater account
  // never gets hidden by a positive total across other accounts.
  const negativeAccounts = cashSources
    .map((s) => {
      const wtc = pendingBySource.get(s.id) || 0;
      const rawBal = Number(s.current_source_balance_optional || 0);
      return { source: s, netAvail: rawBal - wtc };
    })
    .filter(({ netAvail }) => netAvail < 0);
  // Filter out warnings the user dismissed this session. Key is composed
  // of account id + rounded shortfall so the warning re-appears if the
  // shortfall GROWS (which is fresh news the user should re-see).
  const visibleNegativeAccounts = negativeAccounts.filter(
    ({ source, netAvail }) => !dismissedNegKeys.has(`${source.id}:${Math.round(netAvail)}`)
  );

  // ---- Incoming (expected income within the pay period window) ----
  // Prefer the SERVER-computed `expected_income_breakdown` when the parent
  // passes it on `currentPP` (pay-period cards from /api/forecast/pay-periods
  // and the month window from /api/forecast/month both provide this). It's
  // the source of truth: same projection logic, no status-filter bug, no
  // timezone drift. Frontend projection remains as a fallback for any code
  // path that didn't set it (defence in depth).
  // Project every occurrence of a single income row that lands in [start,end].
  //
  // CRITICAL: for recurring series (weekly/biweekly/monthly) we NEVER filter
  // by the base row's `status`. A recurring row's status reflects only the
  // most recent reconciled paycheck — filtering on it would drop every
  // future payday the moment the user marked the latest one received (root
  // cause of the "Step 3 shows $0 in Next 30 days" bug 2026-02).
  //
  // Instead we skip specific dates the user has already marked received.
  // The backend materializes those as separate income rows with
  // status="received"; we key them by (source|date) so the projected
  // occurrence for that same date is suppressed.
  //
  // One-time (unscheduled) income keeps its own status check — a received
  // one-off shouldn't be re-projected.
  const receivedDateKeys = new Set(
    (incomes || [])
      .filter((i) => i.status === "received" && i.expected_date)
      .map((i) => `${(i.source || "").toLowerCase()}|${i.expected_date}`)
  );
  const projectIncomeOccurrences = (inc, start, end) => {
    const freq = inc.frequency;
    const step = { weekly: 7, biweekly: 14, monthly: 30 }[freq];
    const base = new Date(inc.expected_date + "T00:00:00");
    const endDate = inc.end_date ? new Date(inc.end_date + "T00:00:00") : null;
    if (!step) {
      // One-time / unscheduled — only expected rows should render.
      if (inc.status !== "expected") return [];
      return (base >= start && base <= end) ? [inc] : [];
    }
    // Recurring — walk forward regardless of base status. Force projected
    // occurrences to status="expected" so they count toward "Money still
    // coming" and not the already-received bucket.
    const skipDates = new Set(inc.skip_dates || []);
    let cur = new Date(base);
    while (cur < start) cur = new Date(cur.getTime() + step * 86400000);
    // Walk BACKWARD only if we're already inside the window AND still after
    // the base — recurring occurrences never exist before the base date, so
    // we must not project a Nov-9 base into Sep/Oct (bug 2026-02).
    while (cur > start && cur > base && new Date(cur.getTime() - step * 86400000) >= start) {
      cur = new Date(cur.getTime() - step * 86400000);
    }
    const out = [];
    while (cur <= end) {
      if (endDate && cur > endDate) break;
      if (cur >= start) {
        const iso = cur.toISOString().slice(0, 10);
        const key = `${(inc.source || "").toLowerCase()}|${iso}`;
        // Skip a projected occurrence if the user already materialized a
        // received row for that exact date+source, OR the user has
        // explicitly removed this occurrence from the series.
        if (!receivedDateKeys.has(key) && !skipDates.has(iso)) {
          const overrideAmt = inc.overrides && inc.overrides[iso];
          out.push({
            ...inc,
            id: `${inc.id}::${iso}`,
            expected_date: iso,
            status: "expected",
            amount: overrideAmt != null ? Number(overrideAmt) : inc.amount,
            _projected: iso !== inc.expected_date,
            _base_income_id: inc.id,
          });
        }
      }
      cur = new Date(cur.getTime() + step * 86400000);
    }
    return out;
  };
  const serverBreakdown = Array.isArray(currentPP.expected_income_breakdown)
    ? currentPP.expected_income_breakdown
    : null;
  const incomingRaw = serverBreakdown && serverBreakdown.length >= 0
    ? serverBreakdown.map((e) => {
        // When this occurrence lands exactly on the base recurring row's
        // own date, render the REAL row — with real id, real status, and the
        // Mark-received toggle intact. Fixes bug: the base date was showing
        // as "Projected" even though the user had reconciled it, and there
        // was no way to toggle received/expected on the current pay period's
        // paycheck.
        const base = incomes.find((i) => i.id === e.income_id);
        if (base && e.is_base) {
          return { ...base, amount: e.amount };
        }
        return {
          id: `${e.income_id || "inc"}::${e.expected_date}`,
          source: e.source || (base && base.source) || "",
          amount: e.amount,
          expected_date: e.expected_date,
          frequency: e.frequency || (base && base.frequency),
          status: "expected",
          _projected: true,
          _base_income_id: e.income_id,
        };
      })
    : incomes.flatMap((i) => projectIncomeOccurrences(i, periodStart, periodEnd));
  // Dedupe by (source, date) so overlapping series don't double-count.
  const seenInc = new Set();
  const incoming = incomingRaw
    .filter((i) => {
      const k = `${i.source || ""}|${i.expected_date}`;
      if (seenInc.has(k)) return false;
      seenInc.add(k);
      return true;
    })
    .sort((a, b) => a.expected_date.localeCompare(b.expected_date));
  // Split expected vs received. Only EXPECTED counts toward the "Money still
  // coming" total — received paychecks are already in the bank balance the
  // user reflected in Step 1, so counting them again would double the
  // Remaining Balance downstream. Received rows are still displayed in the
  // list so users see the confirmed arrivals, just with a strong visual cue.
  const expectedIncoming = incoming.filter((i) => i.status !== "received");
  const receivedIncoming = incoming.filter((i) => i.status === "received");
  const incomingTotal = expectedIncoming.reduce((s, i) => s + Number(i.amount), 0);
  const receivedTotal = receivedIncoming.reduce((s, i) => s + Number(i.amount), 0);

  // ---- Unpaid bills split into two visible buckets ----
  // Past Due  = still effectively unpaid AND due_date is strictly BEFORE today
  //             (a bill due later today counts as "coming up", not past due).
  // Coming Up = still effectively unpaid AND due_date is today-through-periodEnd.
  // Bills past the periodEnd are future obligations, not part of this forecast.
  const unpaidThisPeriod = bills
    .filter((b) => {
      const d = new Date(b.due_date + "T00:00:00");
      if (d > periodEnd) return false;
      return effectiveStatus(b, today) === "unpaid";
    })
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
  const pastDueBills = unpaidThisPeriod.filter((b) => b.due_date < todayIso);
  const comingUpBills = unpaidThisPeriod.filter((b) => b.due_date >= todayIso);
  const pastDueTotal = pastDueBills.reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);
  const comingUpTotal = comingUpBills.reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);
  const totalUnpaid = pastDueTotal + comingUpTotal;

  const endOfPeriod = availableBank + incomingTotal - totalUnpaid;

  const billsOpen = openBills;

  // Runtime overrides for palette + labels so the same component reads
  // correctly whether we're in pay-period or month view. Note: only the
  // `month` viewMode gets a custom subheader — the pay-period view falls
  // back to the palette-level copy in sections-palette.js, which was
  // updated (Feb 2026) to say "Bills and one-off planned purchases due
  // in this pay period".
  const windowNoun = viewMode === "month" ? "month" : "pay period";
  const billsPalette = viewMode === "month"
    ? { ...SECTION.bills, subheader: "Bills due in this month" }
    : SECTION.bills;
  const incomingPalette = { ...SECTION.incoming, subheader: viewMode === "month" ? "Confirm expected paychecks + income this month" : "Confirm expected paychecks" };

  return (
    <section className="card-soft !p-0 overflow-hidden" data-testid="pay-period-forecast">
      <div className="px-6 pt-5 pb-4 border-b border-[#EBE6E0]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Cash-flow forecast</div>
            <div className="flex flex-col items-start sm:flex-row sm:items-center sm:flex-wrap gap-2 mt-1">
              <h2 className="font-heading text-xl font-medium text-[#2C302E]" data-testid="cpp-title">
                {(() => {
                  const full = viewLabel || `Current Pay Period · ${formatDateRange(currentPP.period_start, currentPP.period_end)}`;
                  const idx = full.indexOf(" · ");
                  if (idx === -1) return full;
                  return (
                    <>
                      <span>{full.slice(0, idx)}</span>
                      <span className="hidden sm:inline"> · </span>
                      <span className="block sm:inline">{full.slice(idx + 3)}</span>
                    </>
                  );
                })()}
              </h2>
              {statusLabel && <StatusBadge status={statusLabel} />}
            </div>
          </div>
          {onViewModeChange && (
            /* Segmented-control toggle between the current pay period,
               a rolling next-30-days window, and the current calendar
               month. Parent owns the state; we just paint. */
            <div
              className="inline-flex items-center rounded-full border border-[#D8CFB9] bg-[#F9F8F6] p-0.5 text-xs whitespace-nowrap self-start overflow-x-auto"
              data-testid="ppf-view-toggle"
              role="group"
              aria-label="Forecast window"
            >
              <button
                type="button"
                onClick={() => onViewModeChange("pay-period")}
                className={`shrink-0 px-3 py-1.5 rounded-full font-semibold transition-colors ${
                  viewMode === "pay-period"
                    ? "bg-[#4A6148] text-white shadow-sm"
                    : "text-[#59605C] hover:text-[#2C302E]"
                }`}
                aria-pressed={viewMode === "pay-period"}
                data-testid="ppf-view-toggle-pay-period"
              >
                This pay period
              </button>
              <button
                type="button"
                onClick={() => onViewModeChange("month")}
                className={`shrink-0 px-3 py-1.5 rounded-full font-semibold transition-colors ${
                  viewMode === "month"
                    ? "bg-[#4A6148] text-white shadow-sm"
                    : "text-[#59605C] hover:text-[#2C302E]"
                }`}
                aria-pressed={viewMode === "month"}
                data-testid="ppf-view-toggle-month"
              >
                This month
              </button>
              <button
                type="button"
                onClick={() => onViewModeChange("next-30")}
                className={`shrink-0 px-3 py-1.5 rounded-full font-semibold transition-colors ${
                  viewMode === "next-30"
                    ? "bg-[#4A6148] text-white shadow-sm"
                    : "text-[#59605C] hover:text-[#2C302E]"
                }`}
                aria-pressed={viewMode === "next-30"}
                data-testid="ppf-view-toggle-next-30"
              >
                Next 30 days
              </button>
            </div>
          )}
        </div>
        <div className="mt-3 flex justify-end">
          <EmailSnapshotButton
            viewMode={viewMode === "month" ? "month" : "pay-period"}
            periodStart={currentPP.period_start}
            periodEnd={currentPP.period_end}
            statusLabel={statusLabel}
            startBalance={availableBank}
            incomingTotal={incomingTotal}
            billsTotal={totalUnpaid}
            endBalance={endOfPeriod}
            billCount={unpaidThisPeriod.length}
          />
        </div>
      </div>

      {/* Money on hand */}
      <div className="mx-6 mt-5 mb-4" data-testid="ppf-section-bank">
        <SectionHeader
          palette={SECTION.bank} open={openBank} onToggle={() => setOpenBank((v) => !v)}
          total={availableBank} sign="" totalLabel="Real bank balance (after pendings clear)"
          testId="ppf-bank"
        />
        {/* Always-visible negative-account warnings. Sit under the Section
            header so they stay on screen even when Step 1 is collapsed —
            critical because the summed "Real bank balance" can look positive
            while one underlying account is quietly underwater. Rendered as
            one card per account with individual dismiss buttons so users
            can hide the ones they've already noted (per-session, per-account,
            keyed on shortfall amount so the warning re-appears if it grows). */}
        {visibleNegativeAccounts.map(({ source, netAvail }) => {
          const key = `${source.id}:${Math.round(netAvail)}`;
          return (
            <div
              key={key}
              className="mt-2 rounded-2xl border-2 border-[#8C3A30] bg-[#FDF2F0] p-3 sm:p-4"
              data-testid={`ppf-bank-negative-warning-${source.id}`}
              role="alert"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#8C3A30] text-white flex items-center justify-center shrink-0 font-heading font-bold">!</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8C3A30]">
                    Account is negative
                  </div>
                  <div className="text-sm text-[#2C302E] mt-1 leading-relaxed">
                    <strong>{source.name}</strong>{" "}
                    {/* Amount is a button — one tap expands Step 1 and
                        scrolls to the offending account so the user lands
                        on the fix instead of hunting for it. Item 3 in the
                        Feb 2026 Dashboard batch. */}
                    <button
                      type="button"
                      onClick={() => {
                        setOpenBank(true);
                        setTimeout(() => {
                          const el = document.querySelector(`[data-testid="ppf-source-balance-${source.id}"]`);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "center" });
                            el.classList.add("ring-2", "ring-[#8C3A30]", "ring-offset-2");
                            setTimeout(() => el.classList.remove("ring-2", "ring-[#8C3A30]", "ring-offset-2"), 1800);
                          }
                        }, 60);
                      }}
                      className="text-[#8C3A30] font-semibold whitespace-nowrap underline decoration-dotted underline-offset-2 hover:text-[#5A251E] focus:outline-none"
                      aria-label={`Jump to ${source.name} to fix the negative balance`}
                      data-testid={`ppf-bank-negative-jump-${source.id}`}
                    >
                      {formatMoney(netAvail)}
                    </button>
                  </div>
                  <div className="text-xs text-[#59605C] mt-1.5">
                    Even if your total bank balance looks positive, this account is underwater once pending charges clear.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => dismissNegKey(key)}
                  className="text-[#8C3A30] hover:text-[#5A251E] shrink-0 -mt-1 -mr-1 w-8 h-8 rounded-full hover:bg-white/60 flex items-center justify-center text-lg leading-none transition-colors"
                  aria-label={`Dismiss warning for ${source.name}`}
                  title="Dismiss for this session — it will re-appear if the shortfall grows or you re-open the app."
                  data-testid={`ppf-bank-negative-warning-dismiss-${source.id}`}
                >×</button>
              </div>
            </div>
          );
        })}
        {openBank && (
        <div className={`${SECTION.bank.subBg} border-x border-b border-[#2C302E] rounded-b-2xl divide-y divide-white/60`}>
          {/* Helper hint — one line, italic. Same visual treatment as Step 2
              and Step 3 for consistency across the three-step forecast. */}
          <div className="py-2 text-xs italic text-[#3E4340]" data-testid="ppf-bank-hint">
            <div className="row-content px-6">
              Type the amount in each account below. Include cash on hand — not just bank accounts.
            </div>
          </div>
          {cashSources.length === 0 ? (
            <div className="py-4" data-testid="ppf-bank-empty">
              <div className="row-content px-6 flex-col items-start gap-2">
                <p className="text-sm text-[#3E4340]">
                  You haven&apos;t added any spending accounts yet. Add your bank accounts <em>and</em> any cash you carry so your forecast starts with the real number.
                </p>
                <Link
                  to="/app/accounts"
                  className="btn-primary text-sm"
                  data-testid="ppf-bank-add-account"
                >
                  <Plus size={14} className="inline -mt-0.5 mr-1" /> Set up your accounts
                </Link>
              </div>
            </div>
          ) : allDisplaySources.map((s) => {
            const wtc = pendingBySource.get(s.id) || 0;
            const rawBal = Number(s.current_source_balance_optional || 0);
            const netAvail = rawBal - wtc;
            const hasPending = wtc > 0;
            const isReserve = !s.contributes_to_total_balance;
            // Category drives the icon + chip label. Legacy accounts with
            // no category default to "General Spending" so nothing breaks.
            const category = s.account_category || "general_spending";
            const CATEGORY_META = {
              bills_planned:    { label: "Bills & Planned",    Icon: Receipt,     iconBg: "bg-[#F5D9D1]", iconFg: "text-[#7A2E24]", chipCls: "bg-[#F5D9D1] text-[#7A2E24]" },
              general_spending: { label: "General Spending",   Icon: Wallet,      iconBg: "bg-[#E8E4DC]", iconFg: "text-[#4A6148]", chipCls: "bg-[#E8E4DC] text-[#4A6148]" },
              savings:          { label: "Savings",            Icon: PiggyBank,   iconBg: "bg-[#DDE9D8]", iconFg: "text-[#1F6B2E]", chipCls: "bg-[#DDE9D8] text-[#1F6B2E]" },
              other:            { label: "Other",              Icon: Landmark,    iconBg: "bg-[#EFEAE0]", iconFg: "text-[#59605C]", chipCls: "bg-[#EFEAE0] text-[#59605C]" },
            };
            const cmeta = CATEGORY_META[category] || CATEGORY_META.general_spending;
            const Icon = cmeta.Icon;
            return (
              <div
                key={s.id}
                className={`mx-3 my-2 rounded-2xl overflow-hidden border-2 ${isReserve ? "border-dashed border-[#D8CFB9] bg-white/50" : "border-[#D8CFB9] bg-white/80"}`}
                data-testid={`ppf-source-block-${s.id}`}
                data-reserve={isReserve ? "1" : "0"}
              >
                <div className="flex items-start gap-3 px-4 py-3" data-testid={`ppf-source-${s.id}`}>
                  <span
                    className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${cmeta.iconBg} ${cmeta.iconFg}`}
                    aria-hidden="true"
                    data-testid={`ppf-source-icon-${s.id}`}
                  >
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#2C302E] truncate flex items-center gap-2 flex-wrap">
                      <span>{s.name}</span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest ${cmeta.chipCls}`}
                        data-testid={`ppf-source-category-${s.id}`}
                      >
                        {cmeta.label}
                      </span>
                      {isReserve && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-[#F0EADB] text-[#7A4E1F] border border-[#D8CFB9]"
                          title="This account is protected — not included in your Real Balance"
                          data-testid={`ppf-source-protected-${s.id}`}
                        >
                          Protected
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#8A918D] mt-0.5 capitalize">
                      {(s.account_type || s.type || "checking").replace("_", " ")}
                    </div>
                    <div className="sm:hidden mt-2 flex items-center justify-between gap-2">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">
                        {isReserve ? "Protected Balance" : "Current Balance"}
                      </div>
                      <AccountBalanceCell
                        source={s}
                        testIdPrefix={`ppf-source-balance-mobile-${s.id}`}
                        onUpdated={onSourceUpdated}
                      />
                    </div>
                  </div>
                  <div className="hidden sm:block text-right leading-tight shrink-0">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">
                      {isReserve ? "Protected Balance" : "Current Balance"}
                    </div>
                    <AccountBalanceCell
                      source={s}
                      testIdPrefix={`ppf-source-balance-${s.id}`}
                      onUpdated={onSourceUpdated}
                    />
                  </div>
                </div>
                {/* Adaptive sub-rows: only render when there are pending
                    charges. Container collapses to just the top row when
                    the account has nothing pending. */}
                {hasPending && !isReserve && (
                  <div className="border-t border-[#EEE7D8] bg-[#FBF3E0]/40" data-testid={`ppf-source-pending-block-${s.id}`}>
                    <button
                      type="button"
                      onClick={() => setDrillIn({
                        kind: "pending",
                        sourceName: s.name,
                        sourceId: s.id,
                        items: pendingItemsBySource.get(s.id) || [],
                        total: wtc,
                      })}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FBF3E0]/70 transition-colors"
                      data-testid={`ppf-source-pending-${s.id}`}
                    >
                      <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-[#FBF3E0] text-[#8A6A1A]" aria-hidden="true">
                        <AlertTriangle size={12} strokeWidth={2} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#7A4E1F]">Bills Pending to Clear</div>
                        <div className="text-[10px] text-[#8A918D]">Click to view what makes up this amount</div>
                      </div>
                      <div className="text-sm font-heading font-bold text-[#7A4E1F] whitespace-nowrap shrink-0">
                        -{formatMoney(wtc)}
                      </div>
                    </button>
                  </div>
                )}
                {hasPending && !isReserve && (
                  <div
                    className="border-t border-[#EEE7D8] px-4 py-2.5 bg-white/60 flex items-center justify-between gap-3"
                    data-testid={`ppf-source-available-${s.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#2C302E]">Available for Spending</div>
                      <div className="text-[10px] text-[#8A918D] mt-0.5">Balance minus bills pending to clear</div>
                    </div>
                    <div className={`text-base font-heading font-bold whitespace-nowrap shrink-0 ${netAvail < 0 ? "text-[#8C3A30]" : "text-[#2C302E]"}`}>
                      {formatMoney(netAvail)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {pendingUnassigned > 0 && (
            <LinkSubRow label="Bills Pending to Clear (unassigned)"
              sublabel="Charges without an account · click to view"
              amount={pendingUnassigned} sign="-"
              palette={SECTION.bank}
              tone="warn"
              variant="unassigned-pending"
              onClick={() => setDrillIn({
                kind: "pending",
                sourceName: "Unassigned (no account linked)",
                items: pendingUnassignedItems,
                total: pendingUnassigned,
              })}
              testId="ppf-pending-unassigned" />
          )}
          {/* Bills & Planned earmark nudge — shows when the user has one or
              more accounts tagged as `bills_planned` but their combined
              balance is less than the total unpaid bills in the current
              period. Not blocking; a soft "heads-up" so the user can top
              up the earmark from Reserve or delay a bill. Hidden if the
              user has NO bills_planned accounts (nothing to compare). */}
          {(() => {
            const hasPlannedAccts = cashSources.some(
              (f) => (f.account_category || "general_spending") === "bills_planned"
            );
            if (!hasPlannedAccts) return null;
            if (billsPlannedTotal >= totalUnpaid) return null;
            const shortfall = totalUnpaid - billsPlannedTotal;
            return (
              <div
                className="mx-3 my-2 rounded-2xl border-2 border-[#E5B4A3] bg-[#FBEEEA] px-4 py-3 flex items-start gap-3"
                data-testid="ppf-earmark-nudge"
              >
                <span className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-[#F5D9D1] text-[#7A2E24]" aria-hidden="true">
                  <AlertTriangle size={16} strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#7A2E24]">
                    Bills &amp; Planned earmark is short
                  </div>
                  <p className="text-xs text-[#59605C] mt-0.5 leading-relaxed">
                    Your Bills &amp; Planned account{cashSources.filter((f) => (f.account_category || "general_spending") === "bills_planned").length === 1 ? "" : "s"} hold{cashSources.filter((f) => (f.account_category || "general_spending") === "bills_planned").length === 1 ? "s" : ""} <strong>{formatMoney(billsPlannedTotal)}</strong> against <strong>{formatMoney(totalUnpaid)}</strong> in unpaid bills. Move <strong>{formatMoney(shortfall)}</strong> in from another account, or reschedule a bill.
                  </p>
                  {/* Reserve Move Shortcut (Feb 2026 D3): quick nav to the
                      Accounts page where the user can adjust the two
                      account balances. `?nudge=earmark` triggers a
                      contextual info banner on that page. */}
                  <Link
                    to="/app/accounts?nudge=earmark"
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#7A2E24] text-white hover:bg-[#5F241C] transition-colors"
                    data-testid="ppf-earmark-adjust-btn"
                  >
                    Adjust accounts →
                  </Link>
                </div>
              </div>
            );
          })()}
          {/* General Spending low nudge (Feb 2026 D3): fires when the
              user has one+ General Spending accounts AND their combined
              balance is at or below `user.general_spending_low_threshold`.
              Threshold defaults to $100 and is user-adjustable in Settings. */}
          {(() => {
            if (generalSpendingAccounts.length === 0) return null;
            if (generalSpendingTotal > generalSpendingThreshold) return null;
            return (
              <div
                className="mx-3 my-2 rounded-2xl border-2 border-[#D8B98A] bg-[#FBF3E0] px-4 py-3 flex items-start gap-3"
                data-testid="ppf-general-spending-nudge"
              >
                <span className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-[#FBF3E0] text-[#8A6A1A] border border-[#D8B98A]" aria-hidden="true">
                  <AlertTriangle size={16} strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#7A4E1F]">
                    General Spending is running low
                  </div>
                  <p className="text-xs text-[#59605C] mt-0.5 leading-relaxed">
                    Your day-to-day money is <strong>{formatMoney(generalSpendingTotal)}</strong> — at or below your comfort threshold of <strong>{formatMoney(generalSpendingThreshold)}</strong>. Consider moving some in from Savings, or slow discretionary spending until next payday.
                  </p>
                  <Link
                    to="/app/settings"
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white text-[#7A4E1F] border border-[#D8B98A] hover:bg-[#F0EADB] transition-colors"
                    data-testid="ppf-general-spending-settings-btn"
                  >
                    Adjust threshold →
                  </Link>
                </div>
              </div>
            );
          })()}
          {/* Reserve summary — shown at the very bottom of Step 1 when the
              user has one or more accounts flagged `contributes_to_total_balance=false`.
              Styled dashed + subdued so it reads as informational rather
              than actionable. Feb 2026 addition (Multi-Account Types). */}
          {reserveSources.length > 0 && (
            <div
              className="mx-3 my-2 rounded-2xl border-2 border-dashed border-[#D8CFB9] bg-white/40 px-4 py-3 flex items-center justify-between gap-3"
              data-testid="ppf-reserve-summary"
            >
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[#7A4E1F]">Reserve · Protected</div>
                <div className="text-sm font-semibold text-[#2C302E] mt-0.5">
                  {reserveSources.length} account{reserveSources.length === 1 ? "" : "s"} not counted toward Real Balance
                </div>
                <div className="text-[11px] text-[#8A918D] mt-0.5">
                  Flip &ldquo;Include in Real Balance&rdquo; from the Accounts page to bring an account back into your day-to-day totals.
                </div>
              </div>
              <div className="text-base font-heading font-bold text-[#2C302E] whitespace-nowrap shrink-0">
                {formatMoney(reserveTotal)}
              </div>
            </div>
          )}
          {/* Bottom TotalRow removed — the header already shows this total,
              so a duplicate at the bottom of the expanded section was pure
              redundancy per user feedback. */}
        </div>
        )}
      </div>

      {/* 2. Unpaid Bills — placed BEFORE incoming so users see the gap
          between what's currently in the bank and what's owed. The
          coverage sub-label below the header spells out how much of the
          bills can be paid from current cash vs. must wait for income. */}
      <div className="mx-6 mb-4" data-testid="ppf-section-bills">
        <SectionHeader
          palette={billsPalette} open={billsOpen} onToggle={() => setOpenBills((v) => !v)}
          total={totalUnpaid} sign="-" totalLabel="Total owed"
          flag={pastDueBills.length > 0}
          testId="ppf-bills"
        />
        {billsOpen && (
        <div className={`${SECTION.bills.subBg} border-x border-b border-[#2C302E] rounded-b-2xl divide-y divide-white/60`}>
          {/* Helper hint — always visible when the section is open. Explains
              WHAT to do here + how the two buckets differ. */}
          <div className="py-2 text-xs italic text-[#7A2E24]" data-testid="ppf-bills-hint">
            <div className="row-content px-6">
              Bills due in this {viewMode === "month" ? "month" : "pay period"}. Click any row to review, or use the buttons below to add / manage bills.
            </div>
          </div>
          {/* Coverage message — the whole point of putting bills before
              incoming. When cash < bills, this tells the user exactly how
              much they need incoming income to cover. */}
          {totalUnpaid > 0 && (
            <div className="py-2 text-xs italic" data-testid="ppf-bills-coverage">
              <div className="row-content px-6">
                {availableBank >= totalUnpaid ? (
                  <span className="text-[#3B5B3E]">
                    ✓ Your current bank balance covers all of these — {formatMoney(availableBank - totalUnpaid)} to spare.
                  </span>
                ) : (
                  <span className="text-[#7A2E24]">
                    Current cash covers {formatMoney(Math.max(0, availableBank))} · you&apos;ll need {formatMoney(totalUnpaid - Math.max(0, availableBank))} from incoming money below.
                  </span>
                )}
              </div>
            </div>
          )}
          {/* Unified bucket list — replaces the old ReconcileBillList /
              LinkRow-fallback split. Renders identically across all three
              view modes (pay-period / month / next-30). Each of the five
              buckets expands INLINE with editable rows; only one open at a
              time. "+ Add Bill" pill lives in the bucket header so users
              can capture a remembered bill without navigating away. Nav to
              the full Bills page still available via the footer link. */}
          <Step2BillBuckets
            bills={bills}
            viewMode={viewMode}
            periodStart={periodStart}
            periodEnd={periodEnd}
            today={today}
            user={user}
            onBillUpdated={onBillUpdated}
            onReschedule={onReschedule}
            setBillStatus={setBillStatus}
            onOpenAddBill={() => setShowBillModal(true)}
            pendingItems={[
              ...Array.from(pendingItemsBySource.values()).flat(),
              ...pendingUnassignedItems,
            ]}
            pendingTotal={pendingTotal}
            onPendingChanged={onPendingChanged}
          />
        </div>
        )}
      </div>

      {/* 3. Money Coming In — arrives DURING this pay period, not yet in
          the bank. Placed after bills so users see it as the thing that
          fills whatever gap current cash can't cover. */}
      <div className="mx-6 mb-5" data-testid="ppf-section-incoming">
        <SectionHeader
          palette={incomingPalette} open={openIncoming} onToggle={() => setOpenIncoming((v) => !v)}
          total={incomingTotal} sign="+" totalLabel="Money still coming"
          testId="ppf-incoming"
          // Small subtitle appears next to the total: helps users see at a
          // glance that some paychecks have ALREADY arrived and are folded
          // into the Step 1 bank balance — not into this "Money still
          // coming" number. Without this, users questioned the discrepancy.
          subtitle={receivedTotal > 0
            ? `+${formatMoney(receivedTotal)} already received`
            : undefined}
        />
        {openIncoming && (
        <div className={`${SECTION.incoming.subBg} border-x border-b border-[#2C302E] rounded-b-2xl divide-y divide-white/60`}>
          <div className="py-2 text-xs italic text-[#2F4E32]" data-testid="ppf-incoming-hint">
            <div className="row-content px-6">
              Arriving during this {viewMode === "month" ? "month" : "period"} · not yet in your bank. Add recurring paychecks or one-time money below.
            </div>
          </div>
          {incoming.length === 0 ? (
            <div className="py-4" data-testid="ppf-incoming-empty">
              <div className="row-content px-6 flex-col items-start gap-2">
                <p className="text-sm text-[#3E4340]">
                  Nothing scheduled to arrive during this {viewMode === "month" ? "month" : "pay period"}. Add your paychecks, side-gig, or a one-time deposit to see the real picture.
                </p>
                <button
                  type="button"
                  className="btn-primary text-sm !py-1.5"
                  onClick={() => setShowIncomeModal(true)}
                  data-testid="ppf-incoming-add-empty-btn"
                >
                  <Plus size={14} className="inline -mt-0.5 mr-1" /> Add income source
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Expected paychecks first — these still need to land.
                  Grouped by source so weekly earners see one collapsible
                  card per employer (with the individual pay events nested)
                  instead of 4+ stacked rows. Preserves all existing
                  IncomeRow edit/received/delete actions. */}
              <GroupedIncomeList
                items={expectedIncoming}
                onChanged={onIncomeChanged}
                testIdPrefix="ppf-incoming-expected"
              />
              {/* Received sub-section — folded away behind a subtle toggle
                  link so the visual spotlight stays on "still coming".
                  Wording adapts to Pay-Period vs Month view per user spec. */}
              {receivedIncoming.length > 0 && (
                <div data-testid="ppf-incoming-received-group">
                  <button
                    type="button"
                    onClick={() => setShowReceivedGroup((v) => !v)}
                    className="w-full py-2 text-left flex items-center gap-1.5"
                    data-testid="ppf-incoming-received-toggle"
                  >
                    <div className="row-content px-6 flex items-center gap-1.5 text-xs text-[#59605C] italic hover:text-[#2F4E32]">
                      {showReceivedGroup ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      <span className="underline decoration-dotted underline-offset-2">
                        {viewMode === "month"
                          ? "Previous Pay Received This Month"
                          : "Previous Pay Received This Pay Period"} (+{formatMoney(receivedTotal)})
                      </span>
                    </div>
                  </button>
                  {showReceivedGroup && (
                    <div data-testid="ppf-incoming-received-list">
                      <GroupedIncomeList
                        items={receivedIncoming}
                        onChanged={onIncomeChanged}
                        testIdPrefix="ppf-incoming-received"
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <AddOneTimeIncomeForm
            periodStart={currentPP.period_start}
            periodEnd={currentPP.period_end}
            onCreated={(created) => onIncomeChanged && onIncomeChanged(created, "create")}
          />
          {/* Always-visible CTA row — Add income source (opens modal) +
              Manage income sources (deep link). Mirrors Step 2's action row. */}
          <div className="py-3 bg-white/60" data-testid="ppf-incoming-actions">
            <div className="row-content px-6 flex items-center justify-between gap-2 flex-wrap">
              <button
                type="button"
                className="btn-primary text-sm !py-1.5"
                onClick={() => setShowIncomeModal(true)}
                data-testid="ppf-incoming-add-btn"
              >
                <Plus size={14} className="inline -mt-0.5 mr-1" /> Add income source
              </button>
              <Link
                to="/app/income"
                className="text-xs font-semibold text-[#2F4E32] hover:underline decoration-dotted underline-offset-2"
                data-testid="ppf-incoming-manage-link"
              >
                Manage all income →
              </Link>
            </div>
          </div>
          {/* Bottom TotalRow removed — header already shows Total incoming. */}
        </div>
        )}
      </div>

      {/* Final result — highlighted with a 3px charcoal border. No section
          number: it's the answer, not a step. Label stacks onto two lines
          so it doesn't overwhelm the amount. */}
      {/* Soft "quick check" nudge — shows only when balances haven't been
          touched in 2+ weeks. Lives just above Step 4 so it directly
          reinforces the freshness stamp below. */}
      {balancesAreStale && (
        <div
          className="mx-6 mb-3 rounded-2xl border border-[#E5B885] bg-[#FFF6E8] px-4 py-3 flex items-start gap-3 flex-wrap"
          data-testid="ppf-eop-stale-nudge"
        >
          <span className="text-lg leading-none mt-0.5" aria-hidden="true">↻</span>
          <div className="text-sm text-[#7A4E1F] leading-relaxed flex-1 min-w-[200px]">
            It&apos;s been <span className="font-semibold">{humanizeDuration(daysSinceEdit)}</span> since you updated your balances — quick check?
          </div>
          <button
            type="button"
            onClick={openStep1AndScroll}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold bg-[#7A4E1F] text-white hover:bg-[#5F3D18] transition-colors shadow-sm whitespace-nowrap"
            data-testid="ppf-eop-stale-update-btn"
          >
            Update balances →
          </button>
        </div>
      )}

      {/* Step 4 · Remaining Balance. Charcoal-outlined tan card so the
          bottom-line number reads as the answer, not another step. Header
          now stamps the calculation with the last time balances/pendings
          were touched so users can trust or refresh the number at a glance. */}
      <div
        className={`${SECTION.final.bg} mx-6 mb-6 border-[3px] border-[#2C302E] rounded-2xl shadow-[0_2px_0_#2C302E20]`}
        data-testid="ppf-eop"
      >
        <div className="py-4">
          <div className="row-content row-connector px-6">
            <div className="min-w-0">
              <div className="font-heading text-lg font-bold text-[#2C302E] leading-tight">
                Your Real Balance!
              </div>
              {balancesLastEditedDate ? (
                <div
                  className={`text-base font-semibold mt-1.5 ${balancesAreStale ? "text-[#B8722A]" : "text-[#4A6148]"}`}
                  data-testid="ppf-eop-updated"
                >
                  Updated on {formatDate(balancesLastEditedIso)}
                  {daysSinceEdit != null && daysSinceEdit > 0 && (
                    <> · {humanizeDaysAgo(daysSinceEdit)}</>
                  )}
                </div>
              ) : (
                <div className="text-base mt-1.5 text-[#8A918D] italic">
                  No balance edits recorded yet
                </div>
              )}
              {balanceEditStreak >= 1 && (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 border border-[#8C5D30]/30 text-xs font-semibold text-[#2C302E] shadow-[0_1px_0_#2C302E14]"
                  data-testid="ppf-eop-streak"
                  aria-label={`Balance edit streak: ${balanceEditStreak} weeks in a row`}
                >
                  <Flame size={12} className="text-[#8C5D30]" fill="currentColor" fillOpacity={0.15} />
                  <span>{balanceEditStreak} week{balanceEditStreak === 1 ? "" : "s"} in a row</span>
                  {balanceEditStreak >= 52 && <span className="text-[#7A4E1F] font-bold">· Master</span>}
                  {balanceEditStreak >= 26 && balanceEditStreak < 52 && <span className="text-[#7A4E1F] font-bold">· Financial regular</span>}
                  {balanceEditStreak >= 12 && balanceEditStreak < 26 && <span className="text-[#4A6148] font-bold">· Steady hand</span>}
                  {balanceEditStreak >= 4 && balanceEditStreak < 12 && <span className="text-[#4A6148] font-bold">· Consistent</span>}
                </div>
              )}
              <div className="text-[10px] text-[#8A918D] mt-1 uppercase tracking-widest">
                Bank + Incoming − Unpaid Bills
              </div>
            </div>
            <span className="row-connector-line" aria-hidden="true" />
            <div className="font-heading text-lg font-bold whitespace-nowrap"
              style={{ color: endOfPeriod < 0 ? "#8C3A30" : "#3B5B3E" }}
              data-testid="ppf-eop-amount">
              {formatMoney(endOfPeriod)}
            </div>
          </div>
        </div>
      </div>

      {showBillModal && (
        <BillModal
          bill={null}
          fundingSources={fundingSources}
          onClose={() => setShowBillModal(false)}
          onSaved={() => {
            setShowBillModal(false);
            toast.success("Bill added");
            onRefresh && onRefresh();
          }}
        />
      )}
      {showIncomeModal && (
        <IncomeModal
          income={null}
          onClose={() => setShowIncomeModal(false)}
          onSaved={(created) => {
            setShowIncomeModal(false);
            if (onIncomeChanged) onIncomeChanged(created, "create");
            else if (onRefresh) onRefresh();
          }}
        />
      )}
      {drillIn && drillIn.kind === "pending" && (
        <PendingDetailModal
          sourceName={drillIn.sourceName}
          sourceId={drillIn.sourceId}
          items={drillIn.items}
          total={drillIn.total}
          onClose={() => setDrillIn(null)}
          onItemRemoved={(item) => {
            // Update local drill-in list so the modal reflects the deletion
            // immediately, then let the parent re-fetch pending payments.
            setDrillIn((prev) => prev ? ({
              ...prev,
              items: prev.items.filter((it) => !(it.kind === item.kind && it.id === item.id)),
              total: Math.max(0, prev.total - Number(item.amount || 0)),
            }) : prev);
            onPendingChanged && onPendingChanged(item, "delete");
          }}
          onItemAdded={(created) => {
            // Prepend the new pending row into the drill-in list + parent refetch.
            const newItem = {
              kind: "pending", id: created.id, name: created.description,
              date: created.expected_clear_date || null,
              amount: Number(created.amount || 0),
              funding_source_id: created.funding_source_id || null,
            };
            setDrillIn((prev) => prev ? ({
              ...prev,
              items: [newItem, ...prev.items],
              total: prev.total + newItem.amount,
            }) : prev);
            onPendingChanged && onPendingChanged(created, "create");
          }}
          onBillCleared={(item) => {
            // A bill going Cleared is no longer "pending to clear" — drop
            // it from the modal's list and re-fetch dashboard state so the
            // three-band list, forecast, and per-account safe-to-spend all
            // recompute.
            setDrillIn((prev) => prev ? ({
              ...prev,
              items: prev.items.filter((it) => !(it.kind === "bill" && it.id === item.id)),
              total: Math.max(0, prev.total - Number(item.amount || 0)),
            }) : prev);
            onPendingChanged && onPendingChanged(item, "cleared");
          }}
          testId="ppf-drill-pending"
        />
      )}
    </section>
  );
}

